import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Calendar, Check, Clock, RefreshCw, User } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Ed, Reveal } from "../components/Editorial";
import { fmtRange, fmtTime, fmtFechaLarga, minutosHasta } from "../lib/time";
import { PHONE } from "../constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Página pública de una reserva, abierta con el enlace del recibo. Deja al
// cliente moverse a otra clase de la misma disciplina sin pasar por el estudio,
// dentro de la ventana sin cargo del contrato (60 minutos o más antes).

const Dato = ({ icon: Icono, children }) => (
  <p className="flex items-center gap-2.5 text-sm text-zinc-400 font-light">
    <Icono size={13} className="shrink-0 text-[#C5A059]" />
    {children}
  </p>
);

const Marco = ({ children }) => (
  <div className="min-h-screen bg-[#050505] pt-32 pb-28 px-6 md:px-16">
    <div className="max-w-3xl mx-auto">{children}</div>
  </div>
);

export default function ManageBooking() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const m = t.manage;

  const [reserva, setReserva] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [eligiendo, setEligiendo] = useState(false);
  const [clases, setClases] = useState([]);
  const [moviendo, setMoviendo] = useState("");
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bookings/${id}/manage`);
      setReserva(r.data);
    } catch {
      setReserva(false);
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirPicker = async () => {
    setEligiendo(true);
    setError("");
    try {
      const r = await axios.get(`${API}/classes?days=14`);
      setClases(r.data || []);
    } catch {
      setError(m.genericError);
    }
  };

  // Solo se ofrecen clases que el servidor va a aceptar: misma disciplina,
  // con sitio para todos los lugares de la reserva y fuera de la hora de corte.
  const opciones = useMemo(() => {
    if (!reserva) return [];
    const n = reserva.bikes.length || 1;
    const sirven = clases.filter(
      (c) =>
        c.kind === reserva.kind &&
        c.id !== reserva.class_id &&
        (c.total_bikes || 0) - (c.booked_count || 0) >= n &&
        minutosHasta(c.date, c.time) >= (reserva.free_window_min || 60)
    );
    const porFecha = new Map();
    for (const c of sirven) {
      if (!porFecha.has(c.date)) porFecha.set(c.date, []);
      porFecha.get(c.date).push(c);
    }
    return [...porFecha.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([fecha, lista]) => ({ fecha, lista: lista.sort((a, b) => a.time.localeCompare(b.time)) }));
  }, [clases, reserva]);

  const mover = async (claseId) => {
    setMoviendo(claseId);
    setError("");
    try {
      await axios.post(`${API}/bookings/${id}/reschedule`, { class_id: claseId });
      setListo(true);
      setEligiendo(false);
      await cargar();
    } catch (e) {
      setError(e.response?.data?.detail || m.genericError);
      await cargar();
    } finally {
      setMoviendo("");
    }
  };

  if (cargando)
    return (
      <Marco>
        <p className="font-serif italic text-xl text-zinc-500">{m.loading}</p>
      </Marco>
    );

  if (!reserva)
    return (
      <Marco>
        <p className="overline-gold font-sans">
          <Ed text={m.overline} goldItalic={false} />
        </p>
        <h1 className="mt-8 font-serif text-4xl md:text-5xl text-white">
          <Ed text={m.heading} />
        </h1>
        <p className="mt-8 max-w-lg text-sm text-zinc-500 font-light leading-relaxed">{m.notFound}</p>
        <Link to="/" className="mt-10 inline-block text-[0.65rem] uppercase tracking-[0.3em] text-[#C5A059]">
          {m.backHome}
        </Link>
      </Marco>
    );

  const activa = reserva.status === "confirmed";
  const esSuelo = reserva.kind === "floor";
  const disciplina = esSuelo ? m.kindFloor : m.kindRide;

  return (
    <Marco>
      <Reveal>
        <p className="overline-gold font-sans">
          <Ed text={m.overline} goldItalic={false} />
        </p>
        <h1 className="mt-8 font-serif text-4xl md:text-6xl text-white leading-[0.95]">
          <Ed text={m.heading} />
        </h1>
      </Reveal>

      {listo && (
        <Reveal className="mt-10">
          <div
            className="border border-[#C5A059]/50 bg-[#C5A059]/[0.07] px-6 py-5 flex items-start gap-3"
            data-testid="reschedule-done"
          >
            <Check size={16} className="mt-0.5 shrink-0 text-[#C5A059]" />
            <div>
              <p className="font-serif italic text-lg text-[#C5A059]">{m.moved}</p>
              <p className="mt-1 text-xs text-zinc-400 font-light">{m.movedBody}</p>
            </div>
          </div>
        </Reveal>
      )}

      {/* Ficha de la reserva */}
      <Reveal className="mt-10">
        <div className="border border-[#232323] bg-[#0C0C0C] p-8 md:p-10" data-testid="manage-card">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <p className="text-[0.6rem] uppercase tracking-[0.25em] text-zinc-600">{m.yourClass}</p>
              <p className="mt-3 font-serif italic text-2xl md:text-3xl text-[#C5A059]">
                {reserva.class_name ? reserva.class_name[lang] : ""}
              </p>
            </div>
            <span
              className={`text-[0.55rem] uppercase tracking-[0.25em] px-3 py-1.5 border ${
                activa ? "border-[#C5A059]/40 text-[#C5A059]" : "border-white/15 text-zinc-500"
              }`}
            >
              {activa ? m.statusConfirmed : m.statusOther}
            </span>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-3.5">
            <Dato icon={Calendar}>
              <span className="capitalize">{fmtFechaLarga(reserva.class_date, lang)}</span>
            </Dato>
            <Dato icon={Clock}>{fmtRange(reserva.class_time, reserva.class_time_end)}</Dato>
            <Dato icon={User}>
              {m.coach}: {reserva.instructor}
            </Dato>
            <Dato icon={RefreshCw}>
              {esSuelo ? m.spots : m.bikes}: {esSuelo ? reserva.bikes.length : reserva.bikes.join(", ")}
            </Dato>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-700">
              {m.receipt} {reserva.receipt_number}
            </p>
            {reserva.rescheduled_at && (
              <p className="text-[0.65rem] text-zinc-600 font-light">
                {m.movedNote.replace("{d}", fmtFechaLarga(reserva.rescheduled_at.slice(0, 10), lang))}
              </p>
            )}
          </div>
        </div>
      </Reveal>

      {error && <p className="mt-6 text-sm text-red-400/90 font-light">{error}</p>}

      {/* Cambiar de clase */}
      {activa && reserva.can_reschedule && !eligiendo && (
        <Reveal className="mt-10">
          <button onClick={abrirPicker} className="btn-gold !px-9 !py-4" data-testid="reschedule-open-btn">
            {m.changeCta}
          </button>
          <p className="mt-4 text-xs text-zinc-600 font-light">
            {m.windowNote.replace("{n}", reserva.free_window_min)}
          </p>
        </Reveal>
      )}

      {activa && !reserva.can_reschedule && (
        <Reveal className="mt-10">
          <div className="border border-dashed border-[#232323] px-6 py-6">
            <p className="text-sm text-zinc-400 font-light leading-relaxed">
              {m.tooLate.replace("{phone}", PHONE.display)}
            </p>
            <a
              href={`tel:${PHONE.tel}`}
              className="mt-5 inline-block border border-[#C5A059]/50 px-7 py-3 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors duration-500"
            >
              {PHONE.display}
            </a>
          </div>
        </Reveal>
      )}

      {eligiendo && (
        <Reveal className="mt-14">
          <p className="overline-gold font-sans">{m.pickTitle}</p>
          <p className="mt-5 max-w-xl text-sm text-zinc-500 font-light leading-relaxed">{m.pickIntro}</p>
          <p className="mt-2 text-xs text-zinc-600 font-light">{m.sameKindNote.split("{kind}").join(disciplina)}</p>

          {opciones.length === 0 ? (
            <p className="mt-8 border border-dashed border-[#232323] px-6 py-6 text-sm text-zinc-500 font-light">
              {m.noOptions}
            </p>
          ) : (
            <div className="mt-8 space-y-10" data-testid="reschedule-options">
              {opciones.map(({ fecha, lista }) => (
                <div key={fecha}>
                  <p className="pb-3 border-b border-white/10 font-serif text-lg text-white capitalize">
                    {fmtFechaLarga(fecha, lang)}
                  </p>
                  <div className="mt-4 grid sm:grid-cols-2 gap-px bg-[#1C1C1C] border border-[#1C1C1C]">
                    {lista.map((c) => {
                      const libres = (c.total_bikes || 0) - (c.booked_count || 0);
                      return (
                        <button
                          key={c.id}
                          onClick={() => mover(c.id)}
                          disabled={!!moviendo}
                          className="group bg-[#0C0C0C] p-5 text-left hover:bg-[#121212] transition-colors duration-300 disabled:opacity-40"
                          data-testid={`reschedule-pick-${c.id}`}
                        >
                          <p className="font-serif text-xl text-white group-hover:text-[#C5A059] transition-colors duration-300">
                            {fmtTime(c.time)}
                          </p>
                          <p className="mt-1 text-[0.7rem] text-zinc-500">{c.name[lang]}</p>
                          <p className="mt-2 text-[0.6rem] uppercase tracking-[0.2em] text-zinc-700">
                            {moviendo === c.id ? m.moving : `${libres} ${esSuelo ? m.spots : m.bikes}`}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setEligiendo(false)}
            className="mt-10 text-[0.65rem] uppercase tracking-[0.3em] text-zinc-500 hover:text-white transition-colors duration-300"
          >
            {m.cancelChange}
          </button>
        </Reveal>
      )}

      <div className="mt-20 pt-8 border-t border-white/10 flex items-center justify-between gap-6 flex-wrap">
        <Link
          to="/"
          className="text-[0.65rem] uppercase tracking-[0.3em] text-zinc-500 hover:text-white transition-colors duration-300"
        >
          {m.backHome}
        </Link>
        <Link to="/account" className="text-[0.65rem] uppercase tracking-[0.3em] text-[#C5A059]">
          {m.myAccount}
        </Link>
      </div>
    </Marco>
  );
}
